import React, { useEffect, useState } from "react";
import { GET_LIST, useClient } from "@peracto/client";
import { toast } from "react-toastify";

const DEFAULT_COLOURS = {
    primaryColour: "#0033A0",
    secondaryColour: "#FDDA24",
    tertiaryColour: "#001E60",
};

export const useBabbageAccountConfiguration = (babbageAccounts = []) => {
    const { client } = useClient();

    // Supporting data
    const [categoryOptions, setCategoryOptions] = useState([]);
    const [categoriesLoading, setCategoriesLoading] = useState(true);
    const [brands, setBrands] = useState([]);
    const [brandsLoading, setBrandsLoading] = useState(false);
    const [t3CategoryOptions, setT3CategoryOptions] = useState([]);
    const [t3CategoriesLoading, setT3CategoriesLoading] = useState(false);

    // Field state
    const [isKeyAccount, setIsKeyAccount] = useState(false);
    const [showStrippedFooter, setShowStrippedFooter] = useState(false);
    const [restrictHaynesProAccess, setRestrictHaynesProAccess] = useState(false);
    const [hideOmnihub, setHideOmnihub] = useState(false);
    const [showOffers, setShowOffers] = useState(false);
    const [vehicleAgePopup, setVehicleAgePopup] = useState(false);
    const [excludeMenuItems, setExcludeMenuItems] = useState([]);
    const [themeColours, setThemeColours] = useState(DEFAULT_COLOURS);
    const [customLogo, setCustomLogo] = useState("");
    const [categoryBrandRestrictions, setCategoryBrandRestrictions] = useState([]);
    const [categoryBrandPriorities, setCategoryBrandPriorities] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [restrictedBrands, setRestrictedBrands] = useState([]);
    const [prioritisedBrands, setPrioritisedBrands] = useState([]);

    // Sync all field state when babbageAccounts data changes
    useEffect(() => {
        if (!babbageAccounts || babbageAccounts.length === 0) return;

        setIsKeyAccount(
            babbageAccounts.find((val) => val.field === "isKeyAccount")?.value || false
        );
        setShowStrippedFooter(
            babbageAccounts.find((val) => val.field === "customBabbageAccountTheme")
                ?.value?.showStrippedFooter === "true"
                ? true
                : false
        );
        setRestrictHaynesProAccess(
            babbageAccounts.find((val) => val.field === "hasHaynesProRevoked")?.value || false
        );
        setHideOmnihub(
            babbageAccounts.find((val) => val.field === "hideOmnihub")?.value || false
        );
        setShowOffers(
            ["true", true].includes(
                babbageAccounts.find((val) => val.field === "showOffers")?.value
            )
        );
        setVehicleAgePopup(
            ["true", true].includes(
                babbageAccounts.find((val) => val.field === "vehicleAgePopup")?.value
            )
        );
        setExcludeMenuItems(
            babbageAccounts.find((val) => val.field === "menuItemsExcluded")?.value || []
        );
        const theme = babbageAccounts.find(
            (val) => val.field === "customBabbageAccountTheme"
        )?.value;
        setThemeColours({
            primaryColour: theme?.primaryColour || DEFAULT_COLOURS.primaryColour,
            secondaryColour: theme?.secondaryColour || DEFAULT_COLOURS.secondaryColour,
            tertiaryColour: theme?.tertiaryColour || DEFAULT_COLOURS.tertiaryColour,
        });
        setCustomLogo(theme?.customLogo || "");
        setCategoryBrandPriorities(
            babbageAccounts
                .find((val) => val.field === "categoryBrandPriorities")
                ?.value?.map((priority) => ({
                    category: priority?.category?.["@id"],
                    brand: priority?.brand?.["@id"],
                    priority: priority?.priority,
                })) || []
        );
        setCategoryBrandRestrictions(
            babbageAccounts
                .find((val) => val.field === "categoryBrandRestrictions")
                ?.value?.map((restriction) => ({
                    category: restriction?.category?.["@id"],
                    brand: restriction?.brand?.["@id"],
                })) || []
        );
        setRestrictedBrands(
            babbageAccounts
                .find((val) => val.field === "categoryBrandRestrictions")
                ?.value?.filter(
                    (restriction) =>
                        restriction?.category?.["@id"] === selectedCategory?.value
                )
                ?.map((restriction) => ({
                    brand: restriction?.brand?.["@id"],
                    category: restriction?.category?.["@id"],
                    label: brands?.find(
                        (brand) => restriction?.brand?.["@id"] === brand?.value
                    )?.label,
                    value: restriction?.brand?.["@id"],
                })) || []
        );
        setPrioritisedBrands(
            babbageAccounts
                .find((val) => val.field === "categoryBrandPriorities")
                ?.value?.filter(
                    (priority) =>
                        priority?.category?.["@id"] === selectedCategory?.value
                )
                ?.map((priority) => ({
                    brand: priority?.brand?.["@id"],
                    category: priority?.category?.["@id"],
                    priority: priority?.priority,
                    id: crypto.randomUUID(),
                })) || []
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [babbageAccounts]);

    // Fetch menu item categories
    useEffect(() => {
        const getCategories = async () => {
            setCategoriesLoading(true);
            try {
                const { data } = await client(GET_LIST, "categories", {
                    id: "categories",
                    filter: { itemsPerPage: 10000 },
                });
                const options = data
                    .map((item) => ({ label: item.heading, value: item.identifier }))
                    .filter((item) => item.value !== "car-parts");
                setCategoryOptions(options);
            } catch (e) {
                console.error(e);
                toast.error(
                    e?.error?.body?.hasOwnProperty("hydra:description")
                        ? e.error.body["hydra:description"]
                        : "There was an error loading categories."
                );
            } finally {
                setCategoriesLoading(false);
            }
        };
        getCategories();
    }, [client]);

    // Fetch brands
    useEffect(() => {
        const getBrands = async () => {
            setBrandsLoading(true);
            try {
                const { data } = await client(GET_LIST, "brands", {
                    page: 1,
                    filter: { itemsPerPage: 10000 },
                });
                setBrands(
                    data?.map((item) => ({
                        label: `${item?.name} (${item?.originId})`,
                        value: item.id,
                    }))
                );
            } catch (e) {
                console.error(e);
                toast.error(
                    e?.message ? e.message : "There was an error loading brands."
                );
            } finally {
                setBrandsLoading(false);
            }
        };
        getBrands();
    }, [client]);

    // Fetch T3 categories
    useEffect(() => {
        const getT3Categories = async () => {
            setT3CategoriesLoading(true);
            try {
                const { data } = await client(
                    GET_LIST,
                    "get-active-plp-level-categories",
                    { id: "get-active-plp-level-categories" }
                );
                const options = data.map((item) => ({
                    label: `${item?.heading} (${item?.originId}) - ${item?.parent?.parent?.parent?.heading} > ${item?.parent?.parent?.heading} > ${item?.parent?.heading}`,
                    displayComponent: (
                        <div className="p-2 hover:bg-blue-100 hover:cursor-pointer">
                            {item?.heading} ({item?.originId})
                            <span style={{ color: "#c8c8c8", fontWeight: 300 }}>
                                {" - "}
                                {item?.parent?.parent?.parent?.heading}
                                {" > "}
                                {item?.parent?.parent?.heading}
                                {" > "}
                                {item?.parent?.heading}
                            </span>
                        </div>
                    ),
                    value: item.id,
                }));
                setT3CategoryOptions(options);
            } catch (e) {
                console.error(e);
                toast.error(
                    e?.error?.body?.hasOwnProperty("hydra:description")
                        ? e.error.body["hydra:description"]
                        : "There was an error loading categories."
                );
            } finally {
                setT3CategoriesLoading(false);
            }
        };
        getT3Categories();
    }, [client]);

    const resetColours = () => setThemeColours(DEFAULT_COLOURS);

    return {
        // Field state + setters
        isKeyAccount, setIsKeyAccount,
        showStrippedFooter, setShowStrippedFooter,
        restrictHaynesProAccess, setRestrictHaynesProAccess,
        hideOmnihub, setHideOmnihub,
        showOffers, setShowOffers,
        vehicleAgePopup, setVehicleAgePopup,
        excludeMenuItems, setExcludeMenuItems,
        themeColours, setThemeColours,
        customLogo, setCustomLogo,
        categoryBrandRestrictions, setCategoryBrandRestrictions,
        categoryBrandPriorities, setCategoryBrandPriorities,
        selectedCategory, setSelectedCategory,
        restrictedBrands, setRestrictedBrands,
        prioritisedBrands, setPrioritisedBrands,
        // Supporting data
        categoryOptions,
        categoriesLoading,
        brands,
        brandsLoading,
        t3CategoryOptions,
        t3CategoriesLoading,
        // Helpers
        resetColours,
    };
};
